import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import { FriendsDataProvider } from './contexts/FriendsDataContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BugReportModal from './components/BugReportModal';
import SplashScreen from './components/SplashScreen';
import { bugReportsAPI } from './services/api';
import html2canvas from 'html2canvas';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OAuthCallback from './pages/OAuthCallback';
import CompleteProfile from './pages/CompleteProfile';
import AddressPrivacySetup from './pages/AddressPrivacySetup';
import Map from './pages/Map';
import Friends from './pages/Friends';
import Profile from './pages/Profile';
import FriendProfile from './pages/FriendProfile';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Legal from './pages/Legal';
import ChildSafety from './pages/ChildSafety';
import MyData from './pages/MyData';
import BugReports from './pages/BugReports';
import Admin from './pages/Admin';
import './App.css';

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

// Public route wrapper (redirect to map if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  // Don't redirect if user just registered (flag will be cleared after navigating to address-privacy-setup)
  const justRegistered = sessionStorage.getItem('justRegistered');
  if (justRegistered) {
    return <>{children}</>;
  }

  return !user ? <>{children}</> : <Navigate to="/map" />;
};

function AppContent() {
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [initialScreenshot, setInitialScreenshot] = useState<{ blob: Blob; preview: string } | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const location = useLocation();

  // Capture screen BEFORE opening modal
  const handleOpenBugReport = useCallback(async () => {
    setIsCapturing(true);
    try {
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
      });

      canvas.toBlob((blob) => {
        if (blob) {
          setInitialScreenshot({
            blob,
            preview: canvas.toDataURL()
          });
        }
        setIsBugModalOpen(true);
        setIsCapturing(false);
      }, 'image/png');
    } catch (err) {
      console.error('Error capturing screenshot:', err);
      // Open modal anyway even if capture fails
      setIsBugModalOpen(true);
      setIsCapturing(false);
    }
  }, []);

  const handleCloseBugModal = useCallback(() => {
    setIsBugModalOpen(false);
    setInitialScreenshot(null);
  }, []);

  const handleBugReportSubmit = async (description: string, screenshot?: Blob) => {
    const pageUrl = window.location.href;
    const browserInfo = navigator.userAgent;

    await bugReportsAPI.create(description, pageUrl, browserInfo, screenshot);
  };

  // Hide footer on certain pages
  const pagesWithoutFooter = ['/map', '/friends', '/profile'];
  const shouldShowFooter = !pagesWithoutFooter.includes(location.pathname);

  return (
    <div className="app">
      <Navbar />
      <div className="app-content">
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />

          {/* Legal pages - publicly accessible */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/child-safety" element={<ChildSafety />} />
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/address-privacy-setup"
            element={
              <ProtectedRoute>
                <AddressPrivacySetup />
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/map"
            element={
              <ProtectedRoute>
                <Map />
              </ProtectedRoute>
            }
          />
          <Route
            path="/friends"
            element={
              <ProtectedRoute>
                <Friends />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:friendId"
            element={
              <ProtectedRoute>
                <FriendProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-data"
            element={
              <ProtectedRoute>
                <MyData />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bug-reports"
            element={
              <ProtectedRoute>
                <BugReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/map" />} />
          <Route path="*" element={<Navigate to="/map" />} />
        </Routes>
        {shouldShowFooter && <Footer />}
      </div>

        {/* Floating Bug Report Button */}
        <button
          className={`floating-bug-button ${isCapturing ? 'capturing' : ''}`}
          onClick={handleOpenBugReport}
          title="Signaler un bug"
          disabled={isCapturing}
        >
          <span className="bug-button-icon">{isCapturing ? '📸' : '🐛'}</span>
          <span className="bug-button-text">{isCapturing ? 'Capture...' : 'Signaler un bug'}</span>
        </button>

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={isBugModalOpen}
        onClose={handleCloseBugModal}
        onSubmit={handleBugReportSubmit}
        initialScreenshot={initialScreenshot}
      />
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on first app load (not on hot reload)
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    return !hasSeenSplash;
  });

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem('hasSeenSplash', 'true');
    setShowSplash(false);
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <FriendsDataProvider>
            <DataCacheProvider>
              {showSplash && (
                <SplashScreen onAnimationComplete={handleSplashComplete} duration={2500} />
              )}
              <Router>
                <AppContent />
              </Router>
            </DataCacheProvider>
          </FriendsDataProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
